import Vue from "vue";
import { mapGetters } from "vuex";
import {
    formatDateTime,
    stringHelper
} from "@/components/mixins";
import { partSearch } from "@/components/mixins/partSearch.ts";
import PartCard from './PartCard/PartCard.vue';
import { ResourceSearchResults }          from "@/components/ResourceLibrary";
import SkeletonLoader from "@/components/Elements/SkeletonLoader.vue";


import CatalogSearch from "@/components/Catalog/CatalogSearch.vue"
import { LoadingSpinner } from "@/components";
import PaginationText from "@/components/Elements/PaginationText.vue";
import PartSearchFacets from "@/components/SubMenus/PartSearchFacets";
import { PartInfo } from "@/components/Elements/PartInfo";
import { SuggestPart } from "@/components/SuggestPart";
import { FeatureFlagService } from "@/services/FeatureFlagService";
import { constants } from "@/const";
import {
    ErrorLogService,
    RecentActivityService,
    PartsService
} from "@/services/";
import DnTable from "@/components/Elements/DnTable/DnTable.vue";

export default Vue.extend({
    name: "PartSearchPage",
    components: {
        LoadingSpinner,
        PartInfo,
        PaginationText,
        SuggestPart,
        CatalogSearch,
        PartCard,
        ResourceSearchResults,
        DnTable,
        PartSearchFacets,
        SkeletonLoader
    },
    mixins: [formatDateTime, stringHelper, partSearch],

    created() {
        this.getRecentPartSearches();
        this.documentsLibraryEnabled = FeatureFlagService.getFeatureEnabled(constants.FEATURE_NAMES.DOCUMENTS_LIBRARY_ENABLED)
        this.showPartSearchFacets = FeatureFlagService.getFeatureEnabled(constants.FEATURE_NAMES.PART_SEARCH_FACETS);
        this.performingSearch = true;
        
        // Restore facets from URL query params or recent component store
        const queryFacets = this.$route.query.facets;
        if (queryFacets) {
            try {
                this.selectedFacets = JSON.parse(queryFacets as string);
            } catch (e) {
                ErrorLogService.LogException(e);
            }
        } else if (this.getRecentComponentName === `PartSearch_Facets_${this.searchTerm}`) {
            this.selectedFacets = this.getRecentComponentData;
        }
        
        // Get/Set part data from store
        this.partNumberFromStore = this.getSearchTerm;
        this.partSearchData = this.getPartSearchData;

        // Determine if this is from a url to prevent infinite loop on use of Back button
        if (this.searchTerm !== this.partNumberFromStore) {
            this.partSearchFromUrl().then(() => {
                this.showPreviewResults = true;
            });
        } else {
            this.validatePartsOnce().then(() => {
                this.applyValidatedPartsAndFacets();
                return this.getPartDetails();
            }).then(() => {
                this.showPreviewResults = true;
            });
        }
    },
    computed: {
        ...mapGetters([
            "user",
            "getContentItem",
            "tenantDetails",
            "activeAccountId",
            "McfCostCenterId",
            "audienceAndExclusions",
            "audienceIds",
            "getSearchTerm",
            "getPartSearchData",
            "getRecentComponentName",
            "getRecentComponentData"
        ]),
        previewParts() {
            return this.parts.length > this.partsPerPage ? this.parts.slice(0, this.partsPerPage) : this.parts.length > 0 ? this.parts : [];
        },
        previewPartImages() {
            return this.partImages.length > this.partsPerPage ? this.partImages.slice(0, this.partsPerPage) : this.partImages.length > 0 ? this.partImages : [];
        },
        previewXrefParts() {
            return this.xrefParts.length > this.xrefPartsPerPage ? this.xrefParts.slice(0, this.xrefPartsPerPage) : this.xrefParts.length > 0 ? this.xrefParts : [];
        },
        previewXrefPartImages() {
            return this.xrefPartImages.length > this.xrefPartsPerPage ? this.xrefPartImages.slice(0, this.xrefPartsPerPage) : this.xrefPartImages.length > 0 ? this.xrefPartImages : [];
        },
        paginatedParts() {
            const startIndex = (this.currentPartPageNumber - 1) * this.partsPerPage;
            const endIndex = startIndex + this.partsPerPage;
            return Array.isArray(this.parts) ? this.parts.slice(startIndex, endIndex) : [];
        },
        paginatedPartImages() {
            const startIndex = (this.currentPartPageNumber - 1) * this.partsPerPage;
            const endIndex = startIndex + this.partsPerPage;
            return Array.isArray(this.partImages) ? this.partImages.slice(startIndex, endIndex) : [];
        },
        paginatedXrefParts() {
            const startIndex = (this.currentXrefPageNumber - 1) * this.xrefPartsPerPage;
            const endIndex = startIndex + this.xrefPartsPerPage;
            return Array.isArray(this.xrefParts) ? this.xrefParts.slice(startIndex, endIndex) : [];
        },
        paginatedXrefPartImages() {
            const startIndex = (this.currentXrefPageNumber - 1) * this.xrefPartsPerPage;
            const endIndex = startIndex + this.xrefPartsPerPage;
            return Array.isArray(this.xrefPartImages) ? this.xrefPartImages.slice(startIndex, endIndex) : [];
        },
        isLoadingPartDetailsBatch() {
            return this.activePartDetailBatchRequests > 0;
        },

    },
    props: {
        searchTerm: {
            type: String,
            default: ""
        },
        activeView: {
            type: String,
            default: 'SEARCH_RESULTS'
        }
    },
    data: () => ({
        recentSearchesAreLoading: true,
        recentSearchesExpanded: true,
        showPartResults: false,
        showXrefResults: false,
        showPreviewResults: false,
        documentsLibraryEnabled: false,
        recentSearchesData: {},
        recentSearchesTableColumns: [
            { key: "SearchTerm", label: "Term" },
            { key: "SearchDt", label: "Last Searched" }
        ],
        selectedFacets: {},
        searchFacets: {},
        facetsRefreshKey: 0,
        performingSearch: true,
        havePerformedInitialSearch: false,
        partSearchData: null,
        partNumberFromStore: "",
        // parts only data
        parts: {},
        partImages: {},
        totalNumberOfParts: 0,
        totalNumberOfPartPages: 0,
        currentPartPageNumber: 1,
        partsPerPage: 10,
        partDetailsBatchSize: 30,
        loadedPartDetailBatchIndexes: [],
        loadingPartDetailBatchIndexes: [],
        activePartDetailBatchRequests: 0,
        // xref parts only data
        xrefParts: {},
        xrefPartImages: {},
        totalNumberOfXrefParts: 0,
        totalNumberOfXrefPartPages: 0,
        currentXrefPageNumber: 1,
        xrefPartsPerPage: 100,
        categoryResults: null,
        categoriesAreLoading: true,
        showPartSearchFacets: false,
        validPartIds: [],
        hasValidatedParts: false
    }),
    watch: {
        currentPartPageNumber() {
            window.scrollTo({ top: 0, behavior: "smooth" });
            this.loadPartDetailsForCurrentPage();
        }
    },
    methods: {
        toggleRecentSearches() {
            this.recentSearchesExpanded = !this.recentSearchesExpanded;
        },
        handleShowCrossResults() {
            if(this.xrefParts.length == 1){
                this.RedirectToPartDetail(this.xrefParts[0].FullPartDetails.OemPartCode)
            }
            this.showXrefResults = true;
            this.showPreviewResults = false;
        },
        handleShowPartResults() {
            if(this.parts.length == 1){
                this.RedirectToPartDetail(this.parts[0].FullPartDetails.OemPartCode)
            }
            this.showPartResults = true;
            this.showXrefResults = false;
            this.showPreviewResults = false;
        },
        handleShowPreviewResults() {
            this.showPartResults = false;
            this.showXrefResults = false;
            this.showPreviewResults = true;
        },
        backToPreviewResults() {
            // Clear selected facets when going back to preview results
            this.selectedFacets = {};
            this.updateFacets();
            this.handleShowPreviewResults();
        },
        async getRecentPartSearches() {
            this.recentSearchesAreLoading = true;
            try {
                let result = await RecentActivityService.getRecentPartSearches(this.user.UserId);
                this.recentSearchesData = result;
            } catch (err) {
                ErrorLogService.LogAndGetErrorMessage(err);
            } finally {
                this.recentSearchesAreLoading = false;
            }
        },
        async partSearchFromUrl() {
            try {
                this.showPreviewResults = false;
                this.havePerformedInitialSearch = false;
                this.performingSearch = true;

                let pageNumber = 1;
                this.partSearchData = await this.getPartResults(pageNumber, pageNumber, this.searchTerm, constants.MAX_ELASTIC_SEARCH);

                this.hasValidatedParts = false;
                await this.validatePartsOnce();
                this.applyValidatedPartsAndFacets();
                await this.getPartDetails();
            } catch (err) {
                ErrorLogService.LogAndGetErrorMessage(err);
            } finally {
                this.havePerformedInitialSearch = true;
                this.performingSearch = false;
            }
        },
        async validatePartsOnce() {
            if (this.hasValidatedParts || !this.partSearchData?.data?.PartSearchResults?.PartResults) {
                return;
            }

            const partIds = this.partSearchData.data.PartSearchResults.PartResults.map((part: any) => part.PartId);

            if (!this.showPartSearchFacets) {
                this.validPartIds = partIds;
                this.hasValidatedParts = true;
                return;
            }

            if (partIds.length === 0) {
                this.validPartIds = [];
                this.hasValidatedParts = true;
                return;
            }

            const validationResponse = await PartsService.ValidateParts(this.activeAccountId, this.user.UserId, partIds);
            const validationResults = validationResponse.data;

            if (validationResults.length <= 1) {
                this.validPartIds = partIds;
            } else {
                this.validPartIds = validationResults
                    .filter((result: any) => !constants.INVALID_PART_VALIDATION_REASONS.includes(result.ValidationReason))
                    .map((result: any) => result.UniqueItemId);
            }

            this.hasValidatedParts = true;
        },
        applyValidatedPartsAndFacets() {
            const validatedPartData = this.getValidatedPartDataWithImages();
            this.parts = validatedPartData.map((item: any) => item.part);
            this.partImages = validatedPartData.map((item: any) => item.image);
            this.searchFacets = this.rebuildSearchFacets(this.parts);
        },
        getValidatedPartDataWithImages() {
            const searchParts = this.partSearchData?.data?.PartSearchResults?.PartResults || [];
            const searchPartImages = Array.isArray(this.partSearchData?.data?.PartImageUrls)
                ? this.partSearchData.data.PartImageUrls
                : [];
            const validPartIdSet = new Set(this.validPartIds);

            return searchParts
                .map((part: any, index: number) => ({
                    part,
                    image: searchPartImages[index]
                }))
                .filter((item: any) => validPartIdSet.has(item.part.PartId));
        },
        async recentPartSearch(searchNumber: string, page: number) {
            try {
                this.havePerformedInitialSearch = false;
                this.performingSearch = true;

                let isQuerySearch = false;
                await this.searchParts(page, page, searchNumber, isQuerySearch);
            } catch (err) {
                ErrorLogService.LogAndGetErrorMessage(err);
            } finally {
                if (this.parts.length > 1) {
                    this.havePerformedInitialSearch = true;
                    this.performingSearch = false;
                }
            }
        },
        async updateParts(partPage: number, xrefPage: number) {
            // used when using pagination buttons
            try {
                this.showPreviewResults = false;
                this.performingSearch = true;
                this.partSearchData = await this.getPartResults(partPage, xrefPage, this.searchTerm, constants.MAX_ELASTIC_SEARCH);

                await this.validatePartsOnce();
                this.applyValidatedPartsAndFacets();
                await this.getPartDetails();
            } catch (err) {
                ErrorLogService.LogAndGetErrorMessage(err);
            } finally {
                this.performingSearch = false;
            }
        },
        async getPartDetails() {
            try {
                this.showPreviewResults = false;
                var self = this;
                let audienceIds = this.audienceAndExclusions ? this.audienceAndExclusions.AudienceIds : null;
                this.loadedPartDetailBatchIndexes = [];
                this.loadingPartDetailBatchIndexes = [];
                this.activePartDetailBatchRequests = 0;

                if (this.havePerformedInitialSearch == false) {
                    this.totalNumberOfParts = this.parts.length;
                    this.totalNumberOfPartPages = Math.ceil(this.totalNumberOfParts / this.partsPerPage);
                }

                this.xrefParts = this.partSearchData.data.CrossReferenceSearchResult.CrossReferenceResults;
                this.xrefPartImages = this.partSearchData.data.XrefPartImageUrls;
                this.totalNumberOfXrefParts = this.partSearchData.data.CrossReferenceSearchResult.NumberOfResults;
                this.totalNumberOfXrefPartPages = this.partSearchData.data.CrossReferenceSearchResult.NumberOfPages;

                this.loadPartDetailsForCurrentPage();

                if (this.xrefParts) {
                    const xrefPartsForDetailLoad = this.havePerformedInitialSearch
                        ? this.xrefParts
                        : this.xrefParts.slice(0, this.partDetailsBatchSize);

                    xrefPartsForDetailLoad.forEach(function (xrefPart: any, index: number) {
                        PartsService.FullPartDetails(self.activeAccountId, self.user.UserId, xrefPart.OriginalPartId, audienceIds).then(fullPartDetail => {
                            //Need to use $set here so that the new FullPartDetails property is added to vue reactivity
                            self.$set(self.xrefParts[index], "FullPartDetails", fullPartDetail.data);
                        }).catch(err => {
                            ErrorLogService.LogException(err);
                        });
                    });
                }
                this.getCategories(this.searchTerm).then(result => {
                    this.categoryResults = result;
                })
                    .catch(err => {
                        ErrorLogService.LogException(err);
                    })
                    .finally(() => {
                        this.categoriesAreLoading = false;
                    });
            } catch (err) {
                ErrorLogService.LogAndGetErrorMessage(err);
            } finally {
                this.recentSearchesAreLoading = false;
                this.havePerformedInitialSearch = true;
                this.performingSearch = false;
            }
        },
        async loadPartDetailsForCurrentPage() {
            if (!Array.isArray(this.parts) || this.parts.length === 0) {
                return;
            }

            const startIndex = (this.currentPartPageNumber - 1) * this.partsPerPage;
            const batchIndex = Math.floor(startIndex / this.partDetailsBatchSize);

            await this.loadPartDetailsForBatch(batchIndex);
        },
        async loadPartDetailsForBatch(batchIndex: number) {
            if (!Array.isArray(this.parts) || this.parts.length === 0) {
                return;
            }

            if (this.loadedPartDetailBatchIndexes.includes(batchIndex) || this.loadingPartDetailBatchIndexes.includes(batchIndex)) {
                return;
            }

            const audienceIds = this.audienceAndExclusions ? this.audienceAndExclusions.AudienceIds : null;
            const startIndex = batchIndex * this.partDetailsBatchSize;
            const endIndex = Math.min(startIndex + this.partDetailsBatchSize, this.parts.length);
            const batchParts = this.parts.slice(startIndex, endIndex);

            this.loadingPartDetailBatchIndexes.push(batchIndex);
            this.activePartDetailBatchRequests++;

            const detailRequests = batchParts
                .map((part: any, index: number) => ({ part, index }))
                .filter(({ part }: any) => !part.FullPartDetails)
                .map(({ part, index }: any) =>
                    PartsService.FullPartDetails(this.activeAccountId, this.user.UserId, part.PartId, audienceIds)
                        .then((fullPartDetail: any) => {
                            // Need to use $set here so that the new FullPartDetails property is added to vue reactivity
                            this.$set(this.parts[startIndex + index], "FullPartDetails", fullPartDetail.data);
                        })
                        .catch((err: any) => {
                            ErrorLogService.LogException(err);
                        })
                );

            try {
                await Promise.all(detailRequests);
                this.loadedPartDetailBatchIndexes.push(batchIndex);
            } finally {
                this.loadingPartDetailBatchIndexes = this.loadingPartDetailBatchIndexes
                    .filter((index: number) => index !== batchIndex);
                this.activePartDetailBatchRequests = Math.max(0, this.activePartDetailBatchRequests - 1);
            }
        },
        ShowSuggestPart() {
            this.activeView = "SUGGEST_PART"
        },
        HideSuggestPart() {
             this.$router.push({ name: "PartSearchPage", query: { searchTerm: this.searchTerm, activeView: 'SEARCH_RESULTS'} });
        },
        ShowStandardPartPagination() {
            return this.performingSearch === false &&
                this.totalNumberOfParts > 0 &&
                this.totalNumberOfPartPages > 1
        },
        navigateToPartSuggestion(){
            this.$router.push({ name: "PartSearchPage", query: { searchTerm: this.searchTerm, activeView: 'SUGGEST_PART'} });
        },
        updateFacets() {
            // Force part-search-facets component to refresh
            this.facetsRefreshKey++;

            // Store facets in store for recent component tracking
            this.$store.commit("SET_RECENT_COMPONENT", {
                name: `PartSearch_Facets_${this.searchTerm}`,
                data: this.selectedFacets,
            });

            try {
                this.currentPartPageNumber = 1;
                this.currentXrefPageNumber = 1;

                // Filter parts by selected facets and validation
                const validatedPartData = this.getValidatedPartDataWithImages();
                const filteredPartData = validatedPartData.filter((item: any) =>
                    this.filterPartsByFacets([item.part], this.selectedFacets).length > 0
                );
                this.parts = filteredPartData.map((item: any) => item.part);
                this.partImages = filteredPartData.map((item: any) => item.image);
                this.searchFacets = this.rebuildSearchFacets(this.parts);
                this.loadedPartDetailBatchIndexes = [];
                this.loadingPartDetailBatchIndexes = [];
                this.activePartDetailBatchRequests = 0;
                this.totalNumberOfParts = this.parts.length;
                this.totalNumberOfPartPages = Math.ceil(this.totalNumberOfParts / this.partsPerPage);
                this.currentPartPageNumber = 1;
                this.loadPartDetailsForCurrentPage();

                // Show all filtered parts if there are facets selected, otherwise show preview results
                if (Object.keys(this.selectedFacets).length > 0) {
                    this.handleShowPartResults();
                } else {
                    this.handleShowPreviewResults();
                }

                // Update URL with selected facets as query parameter
                const facetsString = JSON.stringify(this.selectedFacets);
                const url = new URL(window.location.href);
                url.searchParams.set('searchTerm', this.searchTerm);
                url.searchParams.set('facets', facetsString);
                window.history.pushState(null, '', url.toString());

                this.$nextTick(() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                });
            } catch (err) {
                ErrorLogService.LogAndGetErrorMessage(err);
            }
        },
        filterPartsByFacets(parts: any[], facets: any): any[] {

            if (!facets || Object.keys(facets).length === 0) {
                return parts;
            }
            
            return parts.filter((part: any) => {
                // Check if part has Specs property
                if (!part.Specs || !Array.isArray(part.Specs)) {
                    return false;
                }
                
                // Part must match ALL selected facets (AND logic)
                return Object.keys(facets).every((facetKey: string) => {
                    const selectedValue = facets[facetKey];
                    
                    // Find the spec object with matching name in the Specs array
                    const matchingSpec = part.Specs.find((spec: any) => 
                        spec.name === facetKey || spec.Name === facetKey || 
                        spec.key === facetKey || spec.Key === facetKey ||
                        spec.SpecName === facetKey || spec.specName === facetKey
                    );
                    
                    // Extract the value from the matching spec
                    const specValue = matchingSpec ? 
                        (matchingSpec.value || matchingSpec.Value || matchingSpec.SpecValue) : 
                        undefined;
                    
                     // Must match exactly
                    return specValue === selectedValue;
                });
            });
        },
        rebuildSearchFacets(parts: any[]): any {

            const facets: any = {};
            
            parts.forEach((part: any) => {
                if (part.Specs && Array.isArray(part.Specs)) {
                    part.Specs.forEach((spec: any) => {
                        // Normalize the spec name (handle different naming conventions)
                        const specName = spec.name || spec.Name || spec.key || spec.Key || spec.SpecName || spec.specName;
                        const specValue = spec.value || spec.Value || spec.SpecValue;
                        
                        if (specName && specValue) {
                            // Initialize the facet if it doesn't exist
                            if (!facets[specName]) {
                                facets[specName] = {};
                            }
                            
                            // Track count for each spec value
                            if (!facets[specName][specValue]) {
                                facets[specName][specValue] = 0;
                            }
                            facets[specName][specValue]++;
                        }
                    });
                }
            });
            
            // Convert to array format with Key and Value properties
            const result: any = {};
            Object.keys(facets).forEach((facetKey: string) => {
                result[facetKey] = Object.keys(facets[facetKey]).map((value: string) => ({
                    Key: value,
                    Value: facets[facetKey][value]
                }));
            });
            
            return result;
        }
    }
});
